import {
    AppLaunchMode,
    AppLevelProtocol,
    IsTizenOS,
    IsWebOS,
    IsiDevice,
    PlatformDetails,
    Session,
    Telemetry,
    Usage
} from "./dependencies";
import { MicCapturer } from "./miccapturer";
import { StreamStartParameters } from "./nskinterfaces";
import { StreamKitTelemetryHandler } from "./telemetry/telemetryhandler";
import { RagnarokSettings } from "./util/settings";
import {
    isGamestreamSupported,
    IsResolution1440p,
    IsResolution4k,
    MODE_1080P_HEIGHT,
    MODE_1080P_WIDTH,
    MODE_1440P_HEIGHT,
    MODE_1440P_WIDTH
} from "./util/utils";

/**
 * Singleton instance of Telemetry interface to configure the telemetry upload mechanism.
 * Applications must configure this during NVIDIAStreamKit initialization phase.
 * This singleton instance is responsible for handling telemetry generated from all the library objects and functions.
 */
export const NskTelemetry: Telemetry = StreamKitTelemetryHandler;

/**
 * Returns a string which identifies the current streamer type to the GeForce NOW server components.
 * May not be useful for other use cases.
 * @returns The streamer identification string.
 */
export function getStreamerType(): string {
    return "WEBRTC";
}

/**
 * Feature types supported by browser.
 * Not all browsers support every feature, clients must use @see IsFeatureSupported to determine if the current browser supports the feature.
 */
export declare const enum BrowserFeature {
    /**
     * This feature determines if the browser supports WebRTC streaming and has all the required fixes for optimized streaming.
     * If streaming is not supported then clients must direct users to supported browser and minimum browser version.
     */
    Streaming,
    /** Microphone capture. */
    MicCapture
}

/**
 * Checks whether the browser supports a given feature.
 * @param browserFeature - @see BrowserFeature for different features.
 * @param platformDetails - platform information obtained from @see getPlatformDetails
 * @return - true - if the browser supports the requested feature else false.
 **/
export function isFeatureSupported(
    browserFeature: BrowserFeature,
    platformDetails: PlatformDetails
): boolean {
    switch (browserFeature) {
        case BrowserFeature.Streaming:
            return isGamestreamSupported(platformDetails);
        case BrowserFeature.MicCapture:
            return MicCapturer.isMicSupported();
        default:
            return false;
    }
}

// todo: delete once clients switch to isFeatureSupported.
export { isFeatureSupported as IsFeatureSupported };

/**
 * Informs whether Mic should be enabled by default or not.
 * The recommendation is made based on various criteria like platform permissions, performance, limited support etc.
 * This is not the same as IsFeatureSupported(MicCapture) and cannot be used as a replacement.
 * @param platformDetails - platform info provided by GetPlatformDetails API.
 **/
export function shouldDefaultEnableMic(platformDetails: PlatformDetails): boolean {
    const defaultDisabledOnPlatform =
        IsWebOS(platformDetails) || IsTizenOS(platformDetails) || IsiDevice(platformDetails);
    return !defaultDisabledOnPlatform;
}

/**
 * Provides the recommended range for streaming bitrate in Kilobits per second given the streaming resolution and fps.
 * Clients can provide option to user for selecting the bitrate within this range.
 * User selected value should be passed in @see StreamStartParameters.streamParam.maxBitrateKbps.
 * If user doesn't select any value use maximum bitrate returned by this API.
 * @param width - streaming resolution width.
 * @param height - streaming resolution height.
 * @param fps - streaming framerate.
 * @return - tuple [x, y] - where x is the maximum bitrate and y is the minimum bitrate in kilobits per second.
 */
export function getRecommendedBitrate(
    width: number,
    height: number,
    fps: number
): [number, number] {
    const bIsResolution1440p = IsResolution1440p(width, height);
    const bIsResolution4k = IsResolution4k(width, height);

    const MULTIPLIER_30_FPS = 1.25;
    const adjustedFps = fps === 30 ? fps * MULTIPLIER_30_FPS : Math.min(fps, 60);
    const MIN_BITRATE_Kbps = 50000;

    let bitsPerPixel = 0;

    let maxRateKbps = width * height * adjustedFps;
    if (bIsResolution1440p || bIsResolution4k) {
        bitsPerPixel =
            RagnarokSettings.bitsPerPixel1440p ??
            RagnarokSettings.ragnarokConfig.bitsPerPixel1440p ??
            0.325;

        let maxRateKbps1080p = MODE_1080P_WIDTH * MODE_1080P_HEIGHT * adjustedFps;

        let maxRateKbps1440p = MODE_1440P_WIDTH * MODE_1440P_HEIGHT * adjustedFps;
        maxRateKbps1440p = maxRateKbps1080p + (maxRateKbps1440p - maxRateKbps1080p) / 3;

        if (bIsResolution4k) {
            maxRateKbps = maxRateKbps1440p + (maxRateKbps - maxRateKbps1440p) / 12;
        } else {
            maxRateKbps = maxRateKbps1080p + (maxRateKbps - maxRateKbps1080p) / 3;
        }
    } else {
        bitsPerPixel =
            RagnarokSettings.bitsPerPixel ?? RagnarokSettings.ragnarokConfig.bitsPerPixel ?? 0.3;
    }

    const BITRATE_ADJUSTMENT_FACTOR = 1.2;
    maxRateKbps = (maxRateKbps * bitsPerPixel) / (BITRATE_ADJUSTMENT_FACTOR * 1000);

    if (fps >= 120 && !bIsResolution4k) {
        maxRateKbps = Math.min(MIN_BITRATE_Kbps, Math.round(maxRateKbps * 1.15));
    }

    //todo: we are always setting minimum bitrate to be 5kbps in our recommendation.
    //Eventually we are going to support different minimum bitrate per resolution+fps.
    return [Math.round(maxRateKbps), MIN_BITRATE_Kbps];
}

/**
 * Calculate data usage per hour for streaming at the provided framerate and bitrateKbps values.
 * @param fps - streaming framerate.
 * @param bitrateKbps - Streaming bitrate in Kilo bits per second, @see getRecommendedBitrate.
 * @return - Data usage estimation in GigaBytes per hour.
 **/
export function estimateDataUsage(fps: number, bitrateKbps: number): number {
    // Streaming Bitrate formula (data usage estimate):
    // Default Mode [60Hz]
    //     streamingBitrate = 0.678*maxBitrate + 910569
    // Turbo Mode [120Hz]
    //     streamingBitrate  = 0.916*maxBitrate + 293098
    // Note:
    //  streamingBitrate and maxBitrate should be in the units of [bps: bits per seconds]
    //  GBph [Giga Bytes per hour] = (bps * 3600 * 10^-9) / 8
    //  Mbps [Mega Bits per sec] = (bps * 10^-6)
    //  GBph@30Hz = 0.625 * GBph@60Hz

    const A60 = 0.678;
    const A120 = 0.916;
    const B60 = 910569;
    const B120 = 293098;
    const adjust30 = 0.625;
    const multiplyFactor = 0.95;
    const bitratebps = bitrateKbps * 1000;

    const streamingBitrate = fps === 120 ? A120 * bitratebps + B120 : A60 * bitratebps + B60;
    // convert bps to GB:   GBph [Giga Bytes per hour] = (bps * 3600 * 10^-9) / 8
    let dataUsageGB = (streamingBitrate * 3600) / 1000000000 / 8;

    if (fps === 30) {
        dataUsageGB = dataUsageGB * adjust30;
    }

    // round data usage to integer
    dataUsageGB = dataUsageGB * multiplyFactor;
    dataUsageGB = Math.round(dataUsageGB);

    return dataUsageGB;
}

// Note: Below two APIs are moved from src/util/util.ts.
// This file uses some internal functions from src/util/util.ts.
// Accessing getRecommendedBitrate/estimateDataUsage from above file will result in circular dependency.

/**
 * @deprecated Please use getRecommendedBitrate
 */
export function CalculateMaxBitrateKbps(width: number, height: number, fps: number): number {
    return getRecommendedBitrate(width, height, fps)[0];
}

/**
 * @deprecated Please use estimateDataUsage
 **/
export function CalculateDataUsage(fps: number, bitrateKbps: number) {
    return estimateDataUsage(fps, bitrateKbps);
}

/**
 * Converts the GfnSession object provided by the SessionControl library to StreamStartParameters.
 * Client applications can override the values and update the missing entries.
 * @see StreamStartParameters for which values will be populated, clients need to populate the remaining values.
 * @param session - a stringified SessionControl::Session object returned by SessionControl library.
 * @returns StreamStartParameters which can be updated and passed to startStreaming.
 */
export function getStreamStartParameters(session: string): StreamStartParameters {
    // Note: just doing a JSON parse and type casting should work as long as the Session definition matches between SessionControl and Ragnarok library.
    // Since both SessionControl and Ragnarok will be used from same branch, this should not be a problem for now.
    // Eventually when SessionControl/GridServer is removed from Ragnarok, we will will just access the Session interface.
    // Even if SessionControl library is of later branch than Ragnarok this code should continue to work as we don't delete from our interfaces often.
    // We don't anticipate cases where Ragnarok library is of latest branch but SessionControl is not.
    const gfnSession = JSON.parse(session) as Session;

    const streamStartParams: StreamStartParameters = {
        downstreamParams: [],
        signalConnectionInfo: { address: "", port: 0 },
        enableTouchInput: gfnSession.appLaunchMode === AppLaunchMode.TouchFriendly,
        sessionId: gfnSession.sessionId,
        subSessionId: gfnSession.subSessionId,
        audioTagId: "",
        zoneAddress: gfnSession.zoneAddress,
        statTelemetryInfo: {
            appId: gfnSession.appId,
            zoneName: gfnSession.zoneName,
            gpuType: gfnSession.gpuType
        },
        streamingFeatures: gfnSession.finalizedStreamingFeatures
    };

    for (const monitorSetting of gfnSession.monitorSettings) {
        streamStartParams.downstreamParams.push({
            width: monitorSetting.widthInPixels,
            height: monitorSetting.heightInPixels,
            fps: monitorSetting.framesPerSecond,
            videoTagId: "",
            sdrHdrMode: monitorSetting.sdrHdrMode
        });
    }

    for (const connectionInfo of gfnSession.connectionInfo) {
        switch (connectionInfo.usage) {
            case Usage.SIGNALING:
                streamStartParams.signalConnectionInfo.address = connectionInfo.ip;
                streamStartParams.signalConnectionInfo.port = connectionInfo.port;
                streamStartParams.signalConnectionInfo.secure =
                    connectionInfo.appLevelProtocol === AppLevelProtocol.HTTPS;
                break;
            // SessionControl library provides only VIDEO or BUNDLE port, even if PM provides both VIDEO and BUNDLE port.
            case Usage.VIDEO:
            case Usage.BUNDLE:
                streamStartParams.mediaConnectionInfo = {
                    address: connectionInfo.ip,
                    port: connectionInfo.port
                };
                break;
        }
    }

    return streamStartParams;
}
